import { HUD } from "odyssey-shared";
import { ABILITY_SLOT } from "../bar/layout.ts";
import { BAR_PAGE_SIZE } from "../bar/pages.ts";
import { touchSlot } from "./touch-slots.ts";
import { HUD_SLOTS, TOOL_BELT, resolveSlot, type HudArea, type HudSlot, type HudSlotId, type SlotRect } from "./slots.ts";

/**
 * The HUD's slots on an area (ADR-061's amendments of 2026-10-06 and 2026-10-07, P2.18-T10 and P1.9-T133). `HUD_SLOTS`
 * is the export's grid; on the grid and on every larger area a slot is the table's own. On an area smaller than the
 * grid some slots give way (`slotOn`): under `BAR_PAGES_UNDER` wide the ability bar pages five at a time and the foot
 * group (the bar, the stance button, the XP bar and the cast bar) moves right as far as it must to stand clear of the
 * chat slot, which keeps its own size beside the bar; the toast column keeps a gutter above the menu slot where the
 * area is short; and the maintenance strip keeps clear of the player card and of the chat panel where the area is
 * narrow. A surface reads its place through `slotRect`.
 *
 * Where an area is too small for two surfaces to clear one another by any anchor, the pair is a squeezed share
 * (`SQUEEZED_SHARES`): allowed to overlap only under a stated size, and held clear everywhere else by
 * `area-slots.test.ts`. The chat panel's range and the room an area gives it are here too, since the room follows
 * where the bar stands, and so is the room the development page's diagnostic strip may take (`diagnosticsRoom`).
 */

/**
 * The sizes the chat panel may be resized to, in logical pixels (P1.9-T42; ADR-061's amendment "the chat panel
 * resizes"): from 160 by 90 to 320 by 220, around the slot's own 198 by 122.
 */
export const CHAT_RANGE = { min: { width: 160, height: 90 }, max: { width: 320, height: 220 } } as const;

/**
 * The width under which the ability bar pages five at a time (P1.9-T133; ADR-061's note for P1.9-T133): the least width
 * at which the ten-slot bar, centred on the bottom margin, clears the chat slot beside it, so nothing changes on an
 * area that holds both. Under it the bar shows five slots and the swap control (`PAGED_BAR_WIDTH`).
 */
export const BAR_PAGES_UNDER = 632;

/**
 * The paged bar's width in logical pixels: five slots and the swap control, a slot's size, a slot's gap apart.
 */
export const PAGED_BAR_WIDTH = (BAR_PAGE_SIZE + 1) * ABILITY_SLOT.size + BAR_PAGE_SIZE * ABILITY_SLOT.gap;

/**
 * Whether an area is too narrow for the ten-slot bar beside the chat slot, so the bar pages whatever the viewer chose.
 *
 * @param area The area, whole logical pixels.
 * @returns True under `BAR_PAGES_UNDER` wide.
 * @example
 * barMustPage({ width: 480, height: 500 });
 * // true
 */
export function barMustPage(area: HudArea): boolean {
  return area.touch === true || area.width < BAR_PAGES_UNDER;
}

/**
 * Whether the ability bar pages five at a time on an area: where it must (`barMustPage`), and on any area where the
 * viewer chose Ability bar: Five at a time (`barFive`).
 *
 * @param area The area, whole logical pixels, with the viewer's choice.
 * @returns True where the bar shows five and the swap control.
 * @example
 * barPaged({ width: 960, height: 540, barFive: true });
 * // true
 */
export function barPaged(area: HudArea): boolean {
  return barMustPage(area) || area.barFive === true;
}

/**
 * How far the foot group moves right of centre on an area where the bar pages: as far as the paged bar needs to stand
 * a gutter right of the chat slot, and no further, so from 544 wide it stands centred. Where the bar shows ten,
 * nothing moves.
 *
 * @param area The area, whole logical pixels.
 * @returns The move right, logical pixels.
 */
function footShift(area: HudArea): number {
  if (area.touch === true) {
    return 0;
  }
  const chat = HUD_SLOTS.chat;
  const centred = resolveSlot({ ...HUD_SLOTS.abilities, width: PAGED_BAR_WIDTH }, area).left;
  return Math.max(0, chat.x + chat.width + HUD.gutter - centred);
}

/**
 * A slot of the foot group as it stands where the bar pages: the bar is `PAGED_BAR_WIDTH` wide and the XP bar spans
 * it; the stance button keeps its gutter after the paged bar; all four move right by `footShift`.
 *
 * @param id The slot, one of the foot group.
 * @param slot The table's slot.
 * @param area The area, whole logical pixels.
 * @returns The slot to resolve there.
 */
function pagedFoot(id: "abilities" | "stance" | "xp" | "cast", slot: HudSlot, area: HudArea): HudSlot {
  const shift = footShift(area);
  if (id === "abilities" || id === "xp") {
    return { ...slot, x: slot.x + shift, width: PAGED_BAR_WIDTH };
  }
  if (id === "stance") {
    return { ...slot, x: Math.ceil(PAGED_BAR_WIDTH / 2) + HUD.gutter + Math.floor(ABILITY_SLOT.size / 2) + shift };
  }
  return { ...slot, x: slot.x + shift };
}

/**
 * The tool chip's slot on an area: the table's own where the bar shows ten, and where it pages the gap right of the
 * stance button as it stands there, on the bar's bottom margin.
 *
 * @param slot The table's slot.
 * @param area The area, whole logical pixels.
 * @returns The slot to resolve there.
 */
function toolsOn(slot: HudSlot, area: HudArea): HudSlot {
  if (!barPaged(area)) {
    return slot;
  }
  const stance = slotRect("stance", area);
  return { ...slot, anchor: "bottom-left", x: stance.left + stance.width + TOOL_BELT.gap };
}

/**
 * Where the chat panel's bottom-left corner stands on an area, as distances from the left and bottom edges: the slot's own
 * on a desktop, and on a touch page a gutter above the stick's resting base.
 *
 * @param area The area.
 * @returns The distances.
 */
function chatFoot(area: HudArea): { readonly x: number; readonly y: number } {
  const slot = area.touch === true ? touchSlot("chat", area, HUD_SLOTS.chat) : null;
  return slot === null ? { x: HUD_SLOTS.chat.x, y: HUD_SLOTS.chat.y } : { x: slot.x, y: slot.y };
}

/**
 * The room the chat panel may take on an area, growing up and right from the slot's bottom-left corner without
 * covering a fixed part of the HUD built today. Across: to a gutter left of the ability bar as it stands there (and
 * the XP bar over it), and never less than the slot's own width. Down: from the bottom margin to a gutter under the
 * status row, and never less than the range's least height. Neither passes the area inside the HUD's margins, so a
 * panel never leaves the screen however small the area.
 *
 * @param area The area, whole logical pixels.
 * @returns The most the panel may be across and down there.
 * @example
 * chatRoom({ width: 640, height: 360 });
 * // { width: 200, height: 272 }
 */
export function chatRoom(area: HudArea): { readonly width: number; readonly height: number } {
  const chat = chatFoot(area);
  const status = slotRect("status", area);
  const across = Math.max(HUD_SLOTS.chat.width, slotRect("abilities", area).left - HUD.gutter - chat.x);
  const down = Math.max(CHAT_RANGE.min.height, area.height - chat.y - (status.top + status.height + HUD.gutter));
  return {
    width: Math.max(0, Math.min(across, Math.floor(area.width) - chat.x - HUD.margin)),
    height: Math.max(0, Math.min(down, Math.floor(area.height) - chat.y - HUD.margin)),
  };
}

/**
 * The chat panel at its largest on an area: the range's most, cut to the room there, at the bottom-left corner of
 * the slot. Nothing the panel draws passes it.
 *
 * @param area The area, whole logical pixels.
 * @returns Its rectangle from the area's top left.
 */
export function largestChat(area: HudArea): SlotRect {
  const room = chatRoom(area);
  const width = Math.min(CHAT_RANGE.max.width, room.width);
  const height = Math.min(CHAT_RANGE.max.height, room.height);
  const foot = chatFoot(area);
  return { left: foot.x, top: Math.floor(area.height) - foot.y - height, width, height };
}

/**
 * The room the diagnostic strip may take on an area (P1.9-T132): a development page's line of caption text, hung from
 * the bottom-left corner at the HUD's margin, which is no slot of the table since no player sees it. Across, it stops
 * a gutter short of the ability bar as it stands there, so its line wraps there and never runs over the bar; it wraps
 * upward over the chat panel's corner, which is the panel's own place.
 *
 * @param area The area, whole logical pixels.
 * @returns The most the strip may be across.
 * @example
 * diagnosticsRoom({ width: 480, height: 320 });
 * // { width: 198 }
 */
export function diagnosticsRoom(area: HudArea): { readonly width: number } {
  return { width: Math.max(0, slotRect("abilities", area).left - HUD.gutter - HUD.margin) };
}

/**
 * A slot as it stands on an area: the table's own slot, except for those that give way on an area smaller than the
 * export's grid. Where the bar pages (`barPaged`), the foot group stands as `pagedFoot` says. The chat slot is cut to
 * the room the area gives the panel (`chatRoom`), which is its own size on every area the layouts hold. The toast
 * column, hung from the top at the export's 204, rises so its foot keeps a gutter above the menu slot, and never
 * above the top margin. The maintenance strip, centred at the top, moves right to keep a gutter after the player
 * card, and after the chat panel at its largest wherever that reaches the strip's rows, so the strip never covers the
 * panel's minimise control and grip. On the grid and on any larger area every slot is the table's.
 *
 * @param id The slot.
 * @param area The area, whole logical pixels.
 * @returns The slot to resolve there.
 */
export function slotOn(id: HudSlotId, area: HudArea): HudSlot {
  const slot = HUD_SLOTS[id];
  if (area.touch === true) {
    return touchSlotOn(id, area, slot);
  }
  if (id === "tools") {
    return toolsOn(slot, area);
  }
  if ((id === "abilities" || id === "stance" || id === "xp" || id === "cast") && barPaged(area)) {
    return pagedFoot(id, slot, area);
  }
  if (id === "chat") {
    const room = chatRoom(area);
    return { ...slot, width: Math.min(slot.width, room.width), height: Math.min(slot.height, room.height) };
  }
  if (id === "toasts") {
    const menu = resolveSlot(HUD_SLOTS.menu, area);
    return { ...slot, y: Math.max(HUD.margin, Math.min(slot.y, menu.top - HUD.gutter - slot.height)) };
  }
  if (id === "maintenance") {
    const card = resolveSlot(HUD_SLOTS.vitals, area);
    const chat = largestChat(area);
    const centred = resolveSlot(slot, area).left;
    const pastChat = chat.top < slot.y + slot.height ? chat.left + chat.width + HUD.gutter : 0;
    const left = Math.min(Math.max(centred, card.left + card.width + HUD.gutter, pastChat), Math.max(centred, Math.floor(area.width) - HUD.margin - slot.width));
    return { ...slot, x: slot.x + left - centred };
  }
  return slot;
}

/**
 * A slot on a touch page (`touch-slots.ts`): the touch slot, with the foot group paged and the chat cut to the room its
 * place gives it; a slot the page does not draw is returned as a zero-size slot off the area so a surface that asks draws
 * nothing in view (`touchDrawn` says whether to draw at all).
 *
 * @param id The slot.
 * @param area The area, with `touch` set.
 * @param slot The table's slot.
 * @returns The slot to resolve there.
 */
function touchSlotOn(id: HudSlotId, area: HudArea, slot: HudSlot): HudSlot {
  const touch = touchSlot(id, area, slot);
  if (touch === null) {
    return { ...slot, x: -1000, y: -1000, width: 0, height: 0 };
  }
  if (id === "abilities" || id === "xp") {
    return { ...touch, width: PAGED_BAR_WIDTH };
  }
  if (id === "chat") {
    const room = chatRoom(area);
    return { ...touch, width: Math.min(touch.width, room.width), height: Math.min(touch.height, room.height) };
  }
  return touch;
}

/**
 * Whether the page draws a slot's surface on an area: false for the slots a touch page leaves to its action buttons or
 * does not fill yet (`TOUCH_HIDDEN`), true everywhere else.
 *
 * @param id The slot.
 * @param area The area.
 * @returns True when the surface is drawn.
 */
export function slotDrawn(id: HudSlotId, area: HudArea): boolean {
  return area.touch !== true || touchSlot(id, area, HUD_SLOTS[id]) !== null;
}

/**
 * Where a slot lies on an area, as it stands there (`slotOn`).
 *
 * @param id The slot.
 * @param area The area, whole logical pixels.
 * @returns Its rectangle from the area's top left, whole logical pixels.
 * @example
 * slotRect("toasts", { width: 445, height: 230 });
 * // { left: 313, top: 111, width: 128, height: 84 }
 */
export function slotRect(id: HudSlotId, area: HudArea): SlotRect {
  return resolveSlot(slotOn(id, area), area);
}

/**
 * A pair of slots that may overlap only on an area too small to hold both: under `width` logical pixels across
 * where one is given, and under `height` down where one is given, both where both are. From either size up the
 * pair is held clear like any other.
 */
export interface SqueezedShare {
  readonly pair: readonly [HudSlotId, HudSlotId];
  readonly under: { readonly width?: number; readonly height?: number };
  readonly why: string;
}

/**
 * The squeezed shares, each with the size it is allowed under, which is the least size at which the pair no longer
 * meets (held at its edge by `area-slots.test.ts`). None is met on the export's grid.
 */
export const SQUEEZED_SHARES: readonly SqueezedShare[] = [
  { pair: ["cast", "toasts"], under: { width: 464, height: 380 }, why: "the cast bar, moved right of the chat slot with the paged bar, reaches the toast column's foot; both are passing, and the toast column draws over the cast bar" },
  { pair: ["maintenance", "toasts"], under: { width: 520, height: 237 }, why: "the strip, moved right of the opened chat panel, reaches the toast column's top rows, which only a third stacked toast fills" },
  { pair: ["perf", "message"], under: { width: 679, height: 286 }, why: "the performance panel, 190 tall, reaches a message line of its full width and four lines" },
  { pair: ["perf", "maintenance"], under: { width: 480 }, why: "the performance panel at the top right reaches the centred strip" },
  { pair: ["perf", "maintenance"], under: { width: 536, height: 342 }, why: "the performance panel reaches the strip where it has moved right of the opened chat panel" },
  { pair: ["perf", "toasts"], under: { height: 313 }, why: "the performance panel, 190 tall, leaves less than the toast column's three toasts above the menu slot" },
  { pair: ["tools", "menu"], under: { width: 449 }, why: "the tool chip, right of the stance button on the paged bar, reaches the menu slot at the bottom right corner of the narrowest areas" },
  { pair: ["perf", "cast"], under: { width: 480, height: 272 }, why: "the performance panel, 190 tall, reaches the cast bar moved right of the chat slot with the paged bar" },
];

/**
 * Whether two slots may overlap on an area: one names the other in the table's `shares`, or the pair is a squeezed
 * share and the area is under its size.
 *
 * @param a One slot.
 * @param b Another.
 * @param area The area, whole logical pixels.
 * @returns True for a pair allowed to overlap there.
 */
export function mayShare(a: HudSlotId, b: HudSlotId, area: HudArea): boolean {
  if ((HUD_SLOTS[a].shares ?? []).includes(b) || (HUD_SLOTS[b].shares ?? []).includes(a)) {
    return true;
  }
  return SQUEEZED_SHARES.some(({ pair, under }) => ((pair[0] === a && pair[1] === b) || (pair[0] === b && pair[1] === a)) && (under.width === undefined || area.width < under.width) && (under.height === undefined || area.height < under.height));
}
